import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/Header';
import BreadcrumbNavigation from '../../components/BreadcrumbNavigation';
import QuickActionToolbar from '../../components/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import { negotiationService } from '../../services/negotiationService';
import NewNegotiationModal from './components/NewNegotiationModal';
import ViewNegotiationModal from './components/ViewNegotiationModal';
import EditNegotiationModal from './components/EditNegotiationModal';
import * as exportHelpers from '../../utils/exportHelpers';
import ExportNegotiationsModal from './components/ExportNegotiationsModal';


export default function NegotiationsManagementHub() {
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Load data once on mount
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await negotiationService?.getAll();
        if (mounted) {
          setNegotiations(data || []);
        }
      } catch (err) {
        if (mounted) {
          setError('Erro ao carregar negociações');
          console.error(err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  // Pre-calculate all statuses once when negotiations change
  const negotiationsWithStatus = useMemo(() => {
    return negotiations?.map(negotiation => {
      const receivables = negotiation?.receivables || [];
      let status = 'pending';

      if (receivables?.length === 0) {
        status = negotiation?.payment_status || 'pending';
      } else {
        const statuses = receivables?.map(r => {
          if (!r?.due_date) return r?.status || 'pending';
          if (r?.status === 'paid' || r?.status === 'partial') return r?.status;
          
          const dueDate = new Date(r.due_date);
          const today = new Date();
          today?.setHours(0, 0, 0, 0);
          dueDate?.setHours(0, 0, 0, 0);
          
          if (dueDate < today && r?.status === 'pending') return 'overdue';
          return r?.status || 'pending';
        });

        if (statuses?.every(s => s === 'paid')) {
          status = 'paid';
        } else if (statuses?.some(s => s === 'overdue')) {
          status = 'overdue';
        } else if (statuses?.some(s => s === 'paid') && statuses?.some(s => s === 'pending')) {
          status = 'partial';
        }
      }

      return {
        ...negotiation,
        calculatedStatus: status
      };
    });
  }, [negotiations]);

  // Simplified filtering and pagination with pre-calculated statuses
  const { filteredData, paginatedData, totalPages, stats } = useMemo(() => {
    // Apply filters
    let filtered = negotiationsWithStatus;

    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(neg =>
        neg?.title?.toLowerCase()?.includes(searchLower) ||
        neg?.client?.name?.toLowerCase()?.includes(searchLower) ||
        neg?.destination?.toLowerCase()?.includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(neg => neg?.calculatedStatus === statusFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered?.filter(neg => {
        const saleDate = new Date(neg.sale_date);
        if (dateFilter === 'this-month') {
          return saleDate?.getMonth() === now?.getMonth() && 
                 saleDate?.getFullYear() === now?.getFullYear();
        } else if (dateFilter === 'last-30-days') {
          const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
          return saleDate >= thirtyDaysAgo;
        }
        return true;
      });
    }

    // Calculate stats
    const total = filtered?.length;
    const totalRevenue = filtered?.reduce((sum, neg) => sum + parseFloat(neg?.sale_value || 0), 0);
    const totalProfit = filtered?.reduce((sum, neg) => {
      const sale = parseFloat(neg?.sale_value || 0);
      const cost = parseFloat(neg?.supplier_cost || 0);
      return sum + (sale - cost);
    }, 0);
    const avgValue = totalRevenue / (total || 1);
    const avgMargin = filtered?.reduce((sum, neg) => {
      const sale = parseFloat(neg?.sale_value || 0);
      const cost = parseFloat(neg?.supplier_cost || 0);
      return sum + (sale > 0 ? ((sale - cost) / sale) * 100 : 0);
    }, 0) / (total || 1);

    // Pagination
    const pages = Math.ceil(filtered?.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginated = filtered?.slice(startIndex, startIndex + itemsPerPage);

    return {
      filteredData: filtered,
      paginatedData: paginated,
      totalPages: pages,
      stats: { total, avgValue, totalRevenue, totalProfit, avgMargin }
    };
  }, [negotiationsWithStatus, searchTerm, statusFilter, dateFilter, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter]);

  const handleNewNegotiation = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleNegotiationCreated = async () => {
    try {
      const data = await negotiationService?.getAll();
      setNegotiations(data || []);
    } catch (err) {
      console.error('Error refreshing negotiations:', err);
    }
  };

  const handleViewNegotiation = (negotiation, e) => {
    e?.stopPropagation();
    setSelectedNegotiation(negotiation);
    setOpenDropdown(null);
    setIsViewModalOpen(true);
  };

  const handleEditNegotiation = (negotiation, e) => {
    e?.stopPropagation();
    setSelectedNegotiation(negotiation);
    setOpenDropdown(null);
    setIsEditModalOpen(true);
  };

  const handleDeleteNegotiation = async (negotiation, e) => {
    e?.stopPropagation();
    setOpenDropdown(null);
    
    if (window.confirm(`Tem certeza que deseja excluir a negociação "${negotiation?.title}"?`)) {
      try {
        await negotiationService?.delete(negotiation?.id);
        const data = await negotiationService?.getAll();
        setNegotiations(data || []);
        alert('Negociação excluída com sucesso!');
      } catch (error) {
        alert(`Erro ao excluir negociação: ${error?.message}`);
      }
    }
  };

  const toggleDropdown = (negotiationId, e) => {
    e?.stopPropagation();
    setOpenDropdown(openDropdown === negotiationId ? null : negotiationId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportAllNegotiations = () => {
    exportHelpers?.exportNegotiationsDetailedToCSV(negotiations, 'negociacoes_completo.csv');
    setShowExportModal(false);
  };

  const handleExportCurrentMonth = () => {
    const now = new Date();
    exportHelpers?.exportNegotiationsByMonthToCSV(
      negotiations,
      now?.getMonth(),
      now?.getFullYear()
    );
    setShowExportModal(false);
  };

  const handleExportCustomMonth = (month, year) => {
    exportHelpers?.exportNegotiationsByMonthToCSV(negotiations, month, year);
    setShowExportModal(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      partial: 'bg-info/10 text-info border-info/20',
      paid: 'bg-success/10 text-success border-success/20',
      overdue: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      partial: 'Parcial',
      paid: 'Pago',
      overdue: 'Atrasado'
    };
    return labels?.[status] || status;
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedNegotiation(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedNegotiation(null);
  };

  const handleEditSuccess = async () => {
    try {
      const data = await negotiationService?.getAll();
      setNegotiations(data || []);
    } catch (err) {
      console.error('Error refreshing negotiations:', err);
    }
    setIsEditModalOpen(false);
    setSelectedNegotiation(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground">Carregando negociações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />
      <div className="main-content">
        <BreadcrumbNavigation />
        
        {/* Page Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Icon name="TrendingUp" size={22} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Negociações</h1>
                <p className="text-sm text-muted-foreground">
                  Gerenciamento centralizado de todas as negociações ({filteredData?.length} total)
                </p>
              </div>
            </div>
            <button 
              onClick={handleNewNegotiation}
              className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Icon name="Plus" size={18} />
              <span>Nova Negociação</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por título, cliente ou destino..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="all">Todos os Status</option>
              <option value="pending">Pendente</option>
              <option value="partial">Parcial</option>
              <option value="paid">Pago</option>
              <option value="overdue">Atrasado</option>
            </select>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e?.target?.value)}
              className="px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="all">Todos os Períodos</option>
              <option value="this-month">Este Mês</option>
              <option value="last-30-days">Últimos 30 Dias</option>
            </select>
          </div>
        </div>

        {/* Quick Stats - using pre-calculated stats */}
        <div className="p-6 grid grid-cols-5 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="FileText" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="DollarSign" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor Médio</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(stats?.avgValue)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Receita Total</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(stats?.totalRevenue)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Icon name="PiggyBank" size={20} className="text-emerald-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lucro Total</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {formatCurrency(stats?.totalProfit)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Percent" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Margem Média</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats?.avgMargin?.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Negotiations Grid - using pre-calculated statuses */}
        <div className="p-6">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Negociação
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Destino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Lucro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Margem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedData?.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                            <Icon name="Inbox" size={32} className="text-muted-foreground" />
                          </div>
                          <p className="text-muted-foreground">Nenhuma negociação encontrada</p>
                          <button 
                            onClick={() => {
                              setSearchTerm('');
                              setStatusFilter('all');
                              setDateFilter('all');
                            }}
                            className="text-primary hover:text-primary/80 text-sm"
                          >
                            Limpar filtros
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedData?.map((negotiation) => {
                      const saleValue = parseFloat(negotiation?.sale_value || 0);
                      const cost = parseFloat(negotiation?.supplier_cost || 0);
                      const profit = saleValue - cost;
                      const margin = saleValue > 0 ? ((profit / saleValue) * 100)?.toFixed(1) : 0;

                      return (
                        <tr
                          key={negotiation?.id}
                          className="hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => setSelectedNegotiation(negotiation)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Icon name="FileText" size={18} className="text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{negotiation?.title}</p>
                                <p className="text-xs text-muted-foreground">#{negotiation?.id?.slice(0, 8)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-foreground">{negotiation?.client?.name || '-'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-foreground">{negotiation?.destination || '-'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-foreground">{formatCurrency(saleValue)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className={`text-sm font-medium ${profit >= 0 ? 'text-emerald-500' : 'text-error'}`}>
                              {formatCurrency(profit)}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className={`px-2 py-1 rounded text-xs font-medium ${
                                margin >= 30 ? 'bg-success/10 text-success' :
                                margin >= 15 ? 'bg-warning/10 text-warning': 'bg-error/10 text-error'
                              }`}>
                                {margin}%
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(negotiation?.calculatedStatus)}`}>
                              {getStatusLabel(negotiation?.calculatedStatus)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-muted-foreground">{formatDate(negotiation?.sale_date)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="relative">
                              <button 
                                onClick={(e) => toggleDropdown(negotiation?.id, e)}
                                className="text-muted-foreground hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors"
                              >
                                <Icon name="MoreVertical" size={18} />
                              </button>
                              
                              {openDropdown === negotiation?.id && (
                                <div 
                                  className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-10"
                                  onClick={(e) => e?.stopPropagation()}
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={(e) => handleViewNegotiation(negotiation, e)}
                                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 flex items-center gap-2 transition-colors"
                                    >
                                      <Icon name="Eye" size={16} />
                                      <span>Visualizar</span>
                                    </button>
                                    <button
                                      onClick={(e) => handleEditNegotiation(negotiation, e)}
                                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted/50 flex items-center gap-2 transition-colors"
                                    >
                                      <Icon name="Edit" size={16} />
                                      <span>Editar</span>
                                    </button>
                                    <button
                                      onClick={(e) => handleDeleteNegotiation(negotiation, e)}
                                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 flex items-center gap-2 transition-colors"
                                    >
                                      <Icon name="Trash2" size={16} />
                                      <span>Excluir</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-border bg-muted/20">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredData?.length)} de {filteredData?.length} negociações
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        currentPage === 1
                          ? 'text-muted-foreground cursor-not-allowed bg-muted/50'
                          : 'text-primary hover:bg-primary/10 border border-border'
                      }`}
                    >
                      <Icon name="ChevronLeft" size={16} />
                      <span>Anterior</span>
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                              currentPage === pageNum
                                ? 'bg-primary text-white' :'text-foreground hover:bg-muted/50 border border-border'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        currentPage === totalPages
                          ? 'text-muted-foreground cursor-not-allowed bg-muted/50'
                          : 'text-primary hover:bg-primary/10 border border-border'
                      }`}
                    >
                      <span>Próxima</span>
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickActionToolbar />
      <NewNegotiationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleNegotiationCreated}
      />
      <ViewNegotiationModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        negotiation={selectedNegotiation}
      />
      <EditNegotiationModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
        negotiation={selectedNegotiation}
      />
      {showExportModal && (
        <ExportNegotiationsModal
          onClose={() => setShowExportModal(false)}
          onExportAll={handleExportAllNegotiations}
          onExportCurrentMonth={handleExportCurrentMonth}
          onExportCustomMonth={handleExportCustomMonth}
        />
      )}
    </div>
  );
}